/**
 * Recorder core (WP-F5) — the transport-agnostic, framework-free heart of the recording step.
 *
 * THE BIGGEST TECHNICAL RISK in the plan is iOS Safari `MediaRecorder`, so the two things most
 * likely to break there — MIME negotiation and the upload PUT — live here as pure functions that
 * are unit-tested against simulated browser profiles, away from React and jsdom's missing
 * `MediaRecorder`.
 *
 * Non-negotiable contract this module encodes (ux-flows §2 + design-spec/07 + WP-B12):
 *  - Feature-detect via `MediaRecorder.isTypeSupported` and pick a supported container/codec
 *    (Safari → `audio/mp4`/AAC, Chrome → `audio/webm;codecs=opus`).
 *  - STORE EXACTLY WHAT THE BROWSER PRODUCED; NEVER TRANSCODE — the produced blob's own
 *    `type` and byte length are what we declare to `audio/init` and re-assert at `audio/complete`.
 *  - iOS Safari yields ONE blob at `stop()` (no mid-record chunking), so the recorder collects
 *    whatever `dataavailable` gives and assembles a single blob on stop — never a timeslice.
 *  - A SINGLE retryable constrained PUT of that one blob to the presigned URL (exact content-type;
 *    the browser sets Content-Length from the body to satisfy the signed length).
 */
import type { AudioUploadTicket, RealtimeTranscript, RefereeContext } from './api-types';
import type { RefereeApiClient, RefereeToken } from './api-client';

/* ---------------------------------------------------------------------------------------------- *
 * MIME negotiation
 * ---------------------------------------------------------------------------------------------- */

/**
 * Candidate containers/codecs in preference order. Opus-in-WebM first (smallest, Chrome/Firefox/
 * Android), then MP4/AAC (Safari, incl. iOS), then bare fallbacks. We never force a type the
 * browser rejects — an unsupported list simply defers to the browser's own default.
 */
export const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2', // AAC-LC in MP4 — modern Safari
  'audio/mp4',
] as const;

export type IsTypeSupported = (mimeType: string) => boolean;

/** Real `MediaRecorder.isTypeSupported`, guarded so it is safe to call where MediaRecorder is absent. */
export function browserIsTypeSupported(mimeType: string): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function' &&
    MediaRecorder.isTypeSupported(mimeType)
  );
}

/**
 * Pick the first supported MIME type. `isSupported` is injectable so the matrix (Safari AAC,
 * Chrome Opus, nothing) is exercised deterministically in tests. Returns `undefined` when the
 * browser supports none of our candidates — the caller then constructs `MediaRecorder` with no
 * options and lets the browser choose (and we still store exactly what it produces).
 */
export function negotiateMimeType(
  isSupported: IsTypeSupported = browserIsTypeSupported,
  candidates: readonly string[] = RECORDER_MIME_CANDIDATES,
): string | undefined {
  for (const candidate of candidates) {
    if (isSupported(candidate)) return candidate;
  }
  return undefined;
}

/** Is in-browser recording even possible here? (HTTPS + getUserMedia + MediaRecorder present.) */
export function isRecordingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * The constrained, retryable upload PUT
 * ---------------------------------------------------------------------------------------------- */

export type AudioUploadErrorKind = 'network' | 'rejected' | 'server' | 'aborted';

/** A failed PUT, classified so the caller can decide between "retry" and "re-record". */
export class AudioUploadError extends Error {
  constructor(
    message: string,
    readonly kind: AudioUploadErrorKind,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'AudioUploadError';
  }
}

export interface PutAudioOptions {
  /** Injected fetch (tests / SSR). Defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
  /** Total attempts for the SINGLE PUT (1 = no retry). Default 3. */
  maxAttempts?: number;
  /** Linear backoff base between attempts, ms (× attempt number). Default 600. */
  retryDelayMs?: number;
  signal?: AbortSignal;
  /** Injected sleep so tests don't actually wait between retries. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/**
 * Headers a browser refuses to let script set on a fetch (it sets Content-Length itself from the
 * body). The presign binds an EXACT Content-Length, but because the blob we send is exactly the
 * declared byte length, the browser-computed Content-Length satisfies the signature — so we strip
 * any Content-Length echoed back in the ticket rather than fighting the forbidden-header rule.
 */
const FORBIDDEN_UPLOAD_HEADERS = new Set(['content-length', 'host', 'connection', 'content-transfer-encoding']);

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AudioUploadError('Upload aborted', 'aborted'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new AudioUploadError('Upload aborted', 'aborted'));
      },
      { once: true },
    );
  });
}

function isAbortError(err: unknown): boolean {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) return err.name === 'AbortError';
  return (err as { name?: string } | null)?.name === 'AbortError';
}

/**
 * Upload the recorded blob with a SINGLE PUT (never chunked/multipart), retrying only transient
 * failures. Network errors and 5xx are retried with linear backoff; a 4xx is a deterministic
 * signature/size/type mismatch that retrying cannot fix, so it throws immediately as `rejected`.
 */
export async function putAudioBlob(
  ticket: AudioUploadTicket,
  blob: Blob,
  opts: PutAudioOptions = {},
): Promise<void> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    throw new AudioUploadError('No fetch implementation available for upload', 'network');
  }
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 3);
  const baseDelay = opts.retryDelayMs ?? 600;
  const sleep = opts.sleep ?? defaultSleep;

  const headers = new Headers();
  for (const [name, value] of Object.entries(ticket.headers ?? {})) {
    if (FORBIDDEN_UPLOAD_HEADERS.has(name.toLowerCase())) continue;
    headers.set(name, value);
  }

  let lastError: AudioUploadError = new AudioUploadError('Upload failed', 'network');

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (opts.signal?.aborted) throw new AudioUploadError('Upload aborted', 'aborted');
    try {
      const response = await fetchImpl(ticket.url, {
        method: ticket.method,
        headers,
        body: blob,
        signal: opts.signal,
        // Presigned first-party host — the URL carries its own auth; do not attach the session cookie.
        credentials: 'omit',
      });
      if (response.ok) return;
      if (response.status >= 400 && response.status < 500) {
        throw new AudioUploadError(`Upload rejected (${response.status})`, 'rejected', response.status);
      }
      lastError = new AudioUploadError(`Upload failed (${response.status})`, 'server', response.status);
    } catch (err) {
      if (err instanceof AudioUploadError && (err.kind === 'rejected' || err.kind === 'aborted')) throw err;
      if (isAbortError(err)) throw new AudioUploadError('Upload aborted', 'aborted');
      lastError = new AudioUploadError('Upload network error', 'network');
    }

    if (attempt < maxAttempts) await sleep(baseDelay * attempt, opts.signal);
  }

  throw lastError;
}

/* ---------------------------------------------------------------------------------------------- *
 * Orchestration: init → single PUT → complete
 * ---------------------------------------------------------------------------------------------- */

export interface CompletedRecording {
  /** The one blob the browser produced at stop(). Its `type`/`size` are authoritative. */
  blob: Blob;
  /** Client-measured length; the server re-derives duration authoritatively. */
  durationSeconds: number;
  /** Final turns from the optional realtime rail; absent means batch transcription will run. */
  realtimeTranscript?: RealtimeTranscript;
}

/** The audio subset of the api-client the upload orchestration needs (keeps it seam-agnostic). */
type AudioClient = Pick<RefereeApiClient, 'initAudioUpload' | 'uploadAudio' | 'completeAudioUpload'>;

/**
 * Drive the WP-B12 handshake for a completed take:
 *   init (exact byte length) → single retryable PUT → complete (server HEAD-verifies).
 * Everything is derived from the blob the browser produced — we never re-encode or re-container it.
 */
export async function uploadRecording(
  client: AudioClient,
  token: RefereeToken,
  recording: CompletedRecording,
): Promise<RefereeContext> {
  // EXACTLY what the browser produced — the produced blob's own type and byte length.
  const contentType = recording.blob.type || 'application/octet-stream';
  const contentLength = recording.blob.size;
  const durationSeconds = Math.max(0, Math.round(recording.durationSeconds));

  const ticket = await client.initAudioUpload(token, { contentType, contentLength, durationSeconds });
  // One PUT of the whole blob (retried inside uploadAudio) — never chunked.
  await client.uploadAudio(ticket, recording.blob);
  // Re-assert the exact bytes so the server's HEAD size/type gate lines up with init.
  return client.completeAudioUpload(token, {
    uploadId: ticket.uploadId,
    sizeBytes: contentLength,
    durationSeconds,
    ...(recording.realtimeTranscript ? { realtimeTranscript: recording.realtimeTranscript } : {}),
  });
}

/* ---------------------------------------------------------------------------------------------- *
 * MediaRecorder wrapper (single-blob-at-stop) + level meter
 * ---------------------------------------------------------------------------------------------- */

export type RecorderState = 'inactive' | 'recording' | 'paused';

export interface RecorderResult {
  /** The single assembled blob. Carries the produced MIME type. */
  blob: Blob;
  mimeType: string;
}

export interface RecorderHandle {
  start(): void;
  pause(): void;
  resume(): void;
  /** Stop and resolve with the ONE assembled blob (iOS yields a single blob here). */
  stop(): Promise<RecorderResult>;
  getState(): RecorderState;
  /** Best-effort teardown if the caller abandons the take. */
  dispose(): void;
}

/**
 * Wrap a live `MediaStream` in a MediaRecorder that follows the single-blob model: we do NOT pass
 * a timeslice to `start()`, we collect whatever `dataavailable` events arrive (one on iOS, more on
 * some desktops), and on `stop()` we assemble them into a single blob tagged with the produced MIME.
 */
export function createRecorderSession(stream: MediaStream, mimeType?: string): RecorderHandle {
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  const chunks: BlobPart[] = [];

  recorder.addEventListener('dataavailable', (event) => {
    if (event.data && event.data.size > 0) chunks.push(event.data);
  });

  return {
    start() {
      // No timeslice — one final `dataavailable` is flushed at stop() (iOS-safe).
      recorder.start();
    },
    pause() {
      if (recorder.state === 'recording') recorder.pause();
    },
    resume() {
      if (recorder.state === 'paused') recorder.resume();
    },
    stop() {
      return new Promise<RecorderResult>((resolve, reject) => {
        recorder.addEventListener(
          'stop',
          () => {
            const producedType =
              recorder.mimeType || (chunks[0] as Blob | undefined)?.type || mimeType || '';
            const blob = new Blob(chunks, producedType ? { type: producedType } : undefined);
            resolve({ blob, mimeType: producedType });
          },
          { once: true },
        );
        recorder.addEventListener(
          'error',
          () => reject(new AudioUploadError('Recording failed', 'server')),
          { once: true },
        );
        try {
          recorder.stop();
        } catch (err) {
          reject(err instanceof Error ? err : new AudioUploadError('Recording failed', 'server'));
        }
      });
    },
    getState() {
      return recorder.state as RecorderState;
    },
    dispose() {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* already torn down */
      }
    },
  };
}

export interface LevelMeter {
  /** Instantaneous 0..1 RMS level, scaled for a visual meter. */
  read(): number;
  close(): Promise<void>;
}

interface WebkitWindow {
  webkitAudioContext?: typeof AudioContext;
}

/**
 * A tiny RMS level meter over the live stream, driving the coral waveform. Returns `null` when
 * Web Audio is unavailable (the caller then shows a static, decorative waveform — it is aria-hidden
 * and time-remaining is always text, so nothing accessible depends on it).
 */
export function createLevelMeter(stream: MediaStream): LevelMeter | null {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext ?? (window as unknown as WebkitWindow).webkitAudioContext;
  if (!AudioCtx) return null;

  const context = new AudioCtx();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);
  const buffer = new Uint8Array(analyser.frequencyBinCount);

  return {
    read() {
      analyser.getByteTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) {
        const centred = (buffer[i] - 128) / 128;
        sumSquares += centred * centred;
      }
      const rms = Math.sqrt(sumSquares / buffer.length);
      return Math.min(1, rms * 3.2); // gain so quiet speech still moves the bars
    },
    async close() {
      try {
        source.disconnect();
        await context.close();
      } catch {
        /* context already closed */
      }
    },
  };
}

interface PlaybackNavigator {
  mediaSession?: {
    setMicrophoneActive?: (active: boolean) => Promise<void>;
  };
  audioSession?: {
    type: string;
  };
}

/**
 * Return WebKit's process-wide audio session to ordinary playback after microphone capture.
 *
 * iOS can leave an `<audio>` element on its capture route after every MediaStream track has
 * stopped: the clock advances, but the first playback is silent. Safari exposes two progressively
 * enhanced controls for that hand-off. Both are best-effort because other browsers expose neither.
 */
export async function prepareAudioPlayback(
  browserNavigator: PlaybackNavigator | undefined =
    typeof navigator === 'undefined' ? undefined : (navigator as unknown as PlaybackNavigator),
): Promise<void> {
  if (!browserNavigator) return;

  try {
    await browserNavigator.mediaSession?.setMicrophoneActive?.(false);
  } catch {
    // Capture is already inactive, or this experimental Safari API is unavailable for the page.
  }

  try {
    const audioSession = browserNavigator.audioSession;
    if (!audioSession) return;
    // This playback -> auto pulse is WebKit's current route-reset workaround after getUserMedia.
    audioSession.type = 'playback';
    audioSession.type = 'auto';
  } catch {
    // Non-standard API: never block a usable recording when a browser rejects the assignment.
  }
}
